import React, { useContext } from "react";
import PropTypes from "prop-types";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import styles from "./SearchResultsTable.module.scss";
import { withTranslation } from "react-i18next";
import { BRAND_NAMES, LINCOLN_ENGLISH } from "../../common/Constants";
import fieldViewSearchTableStyles from "./FieldViewSearchTableStyles";
import { publish } from "../../common/PubSub";
import {
  IS_CANADA_MARKET,
  IS_FORD_BRAND,
  IS_USA_MARKET,
} from "../../utils/EmpUtil";
import TitleCase from "../../utils/TitleCase";
import EMPButton from "../../shared/components/EMPButton/EMPButton";
import MotionChevronRight from "../../shared/Icons/MotionChevronRight";
import { UserContext } from "../../shared/context/userContext";

const FieldViewOrderIdSearchResultsTable = (props) => {
  const userContext = useContext(UserContext);

  const onSubmit = (dealer) => {
    // Early return if dealer is null/undefined
    if (!dealer) {
      return;
    }

    // updateCurrentDealer derives and sets brand/dealerBrand from dealer.commonId
    props.user.updateCurrentDealer(dealer);
    publish("brandChanged", props.user.brand);

    // Update the UserContext with the new dealer data for temporary emulation
    // Safely check if context and updateUser are available
    if (userContext?.updateUser && dealer) {
      const { updateUser } = userContext;
      // Create updated user context data with dealer information
      const updatedUserContextData = {
        ...props.user, // Include existing user data
        // Override with dealer-specific properties for emulation
        dealerId: dealer.paCode,
        salesCode: dealer.salesCode,
        brand: props.user.brand,
        dealerName: dealer.name || dealer.dealerName,
        dealerType: dealer.dealerType,
        commonDealerId: dealer.commonId,
        commonId: dealer.commonId,
        // Ensure market is preserved from original user
        market: props.user.market,
        // Keep other essential user properties
        userId: props.user.userId,
        userType: props.user.userType,
        permissions: props.user.permissions,
        roles: props.user.roles,
      };

      // Update the UserContext with the emulated dealer data
      updateUser(updatedUserContextData);
    }

    // Handle language settings based on market and brand
    if (IS_USA_MARKET(props.user.market)) {
      if (IS_FORD_BRAND(props.user.brand)) {
        props.i18n.changeLanguage("en");
      } else {
        props.i18n.changeLanguage(LINCOLN_ENGLISH);
      }
    }
    props.user.setLanguageCode(props.i18n.language);
  };

  const formatPostalCode = (postalCode) => {
    if (postalCode) {
      return postalCode.substring(0, 5);
    }
    return undefined;
  };

  const translateOrderType = (reservation) => {
    //Purchase Request
    if (reservation.purchaseType || reservation.purchaseRequestDate) {
      return props.t("CustomerReservations.purchaseRequest");
    } else {
      return props.t("CustomerReservations.order");
    }
  };

  const getBrandName = (dealerData) => {
    if (dealerData?.commonId) {
      return IS_FORD_BRAND(
        dealerData.commonId.charAt(dealerData.commonId.length - 1)
      )
        ? TitleCase(BRAND_NAMES.FORD)
        : TitleCase(BRAND_NAMES.LINCOLN);
    }
    return "--";
  };

  return (
    <div key={props.index}>
      <TableContainer component={Paper} square>
        <Table id="newTable" key={props.index}>
          <TableHead className={styles.tableHead}>
            <TableRow>
              <TableCell
                data-testid={"orderType" + props.commonDealerId}
                style={fieldViewSearchTableStyles.orderTypeHeader}
              >
                <div className={styles.alignStart}>
                  {props.t("CustomerReservations.orderType")}
                </div>
              </TableCell>
              <TableCell style={fieldViewSearchTableStyles.customerNameHeader}>
                <div className={styles.alignStart}>
                  {props.t("fieldView.customerName")}
                </div>
              </TableCell>
              <TableCell style={fieldViewSearchTableStyles.specsHeader}>
                <div className={styles.alignStart}>
                  {props.t("fieldView.vehicleSpecs")}
                </div>
              </TableCell>
              <TableCell style={fieldViewSearchTableStyles.cellHeader}>
                <div className={styles.alignStart}>
                  {props.t("fieldView.dealerName")}
                </div>
              </TableCell>
              <TableCell style={fieldViewSearchTableStyles.cellHeader}>
                <div className={styles.alignStart}>
                  {props.t("fieldView.salesCode")}
                </div>
              </TableCell>
              <TableCell style={fieldViewSearchTableStyles.cellHeader}>
                <div className={styles.alignStart}>
                  {props.t("fieldView.paCode")}
                </div>
              </TableCell>
              {IS_CANADA_MARKET(props.user.market) && (
                <TableCell style={fieldViewSearchTableStyles.cellHeader}>
                  <div className={styles.alignStart}>
                    {props.t("fieldView.brand")}
                  </div>
                </TableCell>
              )}
              <TableCell style={fieldViewSearchTableStyles.cellHeader}>
                <div className={styles.alignStart}>
                  {props.t("fieldView.dealerAddress")}
                </div>
              </TableCell>
              <TableCell style={fieldViewSearchTableStyles.emptyHeader} />
            </TableRow>
          </TableHead>
          <TableBody
            data-testid="reservationTable"
            className={styles.tableBody}
          >
            {props.reservations.map((reservation, index) => (
              <TableRow
                className="pagePaddingLeft pagePaddingRight"
                id={"row" + index}
                key={reservation.id}
              >
                <TableCell style={fieldViewSearchTableStyles.orderType}>
                  <div style={fieldViewSearchTableStyles.orderTypeText}>
                    {translateOrderType(reservation)}
                  </div>
                  <div style={fieldViewSearchTableStyles.reservationIdText}>
                    {reservation.id}
                  </div>
                </TableCell>
                <TableCell style={fieldViewSearchTableStyles.cellContent}>
                  {reservation.customerName || "--"}
                </TableCell>
                <TableCell style={fieldViewSearchTableStyles.specsORA}>
                  <div className={fieldViewSearchTableStyles.specsWrapperORA}>
                    <div className={styles.nameplate}>
                      {reservation.nameplate}
                    </div>
                    <span className={styles.specs}>
                      {reservation.specSummary?.filter(Boolean).join(" / ")}
                    </span>
                  </div>
                </TableCell>
                <TableCell style={fieldViewSearchTableStyles.cellContent}>
                  <div style={fieldViewSearchTableStyles.dealerNameText}>
                    {reservation.dealerName || "--"}
                  </div>
                </TableCell>
                <TableCell style={fieldViewSearchTableStyles.codeContent}>
                  {props.dealerData?.salesCode
                    ? props.dealerData.salesCode
                    : "--"}
                </TableCell>
                <TableCell style={fieldViewSearchTableStyles.codeContent}>
                  {props.dealerData?.paCode ? props.dealerData.paCode : "--"}
                </TableCell>
                {IS_CANADA_MARKET(props.user.market) && (
                  <TableCell style={fieldViewSearchTableStyles.cellContent}>
                    {getBrandName(props.dealerData)}
                  </TableCell>
                )}
                <TableCell style={fieldViewSearchTableStyles.cellContent}>
                  <div className={fieldViewSearchTableStyles.addressText}>
                    {props.dealerData?.address && (
                      <>
                        <span>{props.dealerData.address.street}, </span>
                        <span>
                          {props.dealerData.address.city},{" "}
                          {props.dealerData.address.state ||
                            props.dealerData.address.province}
                          ,{" "}
                          {IS_USA_MARKET(props.user.market)
                            ? formatPostalCode(
                                props.dealerData.address.postalCode
                              )
                            : props.dealerData.address.postalCode}
                          , {props.dealerData.address.country}
                        </span>
                      </>
                    )}
                  </div>
                </TableCell>
                <TableCell style={fieldViewSearchTableStyles.view}>
                  <EMPButton
                    testId="fieldViewOrderLink"
                    variant="text"
                    rightIcon={<MotionChevronRight />}
                    id={reservation.id}
                    onClick={() => {
                      props.history.push(`customer-handling/${reservation.id}`);
                      onSubmit(props.dealerData);
                    }}
                  >
                    {props.t("fieldView.viewOrder")}
                  </EMPButton>
                  <EMPButton
                    testId="fieldViewDealerLink"
                    variant="text"
                    rightIcon={<MotionChevronRight />}
                    onClick={() => {
                      props.history.push("/dashboard");
                      onSubmit(props.dealerData);
                    }}
                  >
                    {props.t("fieldView.viewDealer")}
                  </EMPButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

FieldViewOrderIdSearchResultsTable.propTypes = {
  commonDealerId: PropTypes.string,
  dealerData: PropTypes.shape({
    address: PropTypes.shape({
      city: PropTypes.string,
      country: PropTypes.string,
      postalCode: PropTypes.string,
      province: PropTypes.string,
      state: PropTypes.string,
      street: PropTypes.string,
    }),
    commonId: PropTypes.string,
    dealerName: PropTypes.string,
    dealerType: PropTypes.string,
    name: PropTypes.string,
    paCode: PropTypes.string,
    salesCode: PropTypes.string,
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  index: PropTypes.number,
  i18n: PropTypes.shape({
    changeLanguage: PropTypes.func.isRequired,
    language: PropTypes.string,
  }).isRequired,
  reservations: PropTypes.arrayOf(
    PropTypes.shape({
      customerName: PropTypes.string,
      dealerId: PropTypes.string,
      dealerName: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      nameplate: PropTypes.string,
      purchaseRequestDate: PropTypes.string,
      purchaseType: PropTypes.string,
      specSummary: PropTypes.array,
    })
  ).isRequired,
  t: PropTypes.func.isRequired,
  user: PropTypes.shape({
    brand: PropTypes.string,
    market: PropTypes.string,
    permissions: PropTypes.object,
    roles: PropTypes.array,
    setLanguageCode: PropTypes.func.isRequired,
    updateBrand: PropTypes.func.isRequired,
    updateCurrentDealer: PropTypes.func.isRequired,
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    userType: PropTypes.string,
  }).isRequired,
};

export default withTranslation("emp")(FieldViewOrderIdSearchResultsTable);
